import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isoWeek from 'dayjs/plugin/isoWeek';
import { ILanguage, useAppStore } from '../stores/app';
import { isProd } from '@/config';
dayjs.extend(duration);
dayjs.extend(isoWeek);

/**
 * Prod defaults to the last 7 days to keep the initial interaction list small.
 * Dev defaults to a 1-year window for testing against sparse seed data.
 */
export const defaultDateRange = (): { start: string; end: string } => {
    const today = dayjs();

    if (isProd) {
        return {
            start: today.subtract(6, 'day').format('YYYY-MM-DD'),
            end: today.format('YYYY-MM-DD'),
        };
    }

    return {
        start: today.subtract(1, 'year').add(1, 'day').format('YYYY-MM-DD'),
        end: today.format('YYYY-MM-DD'),
    };
};

/** Always Monday-start regardless of locale, matching the backend's relative-range convention. */
export const startOfWeekMonday = (): dayjs.Dayjs => dayjs().startOf('isoWeek');

export const dateToString = (
    date: Date | string,
    language: ILanguage | null = null,
    time = true
) => {
    if (typeof date === 'string') {
        date = new Date(date);
    }

    if (!language) {
        const app = useAppStore();
        language = app.language;
    }

    // TODO: Is localeOption needed, or is 'sv' or 'en' sufficient?

    let localeOption = '';

    switch (language) {
        case 'sv':
            localeOption = 'sv-SE';
            break;
        case 'en':
            localeOption = 'en-GB';
            break;
        case 'no':
            localeOption = 'nb-NO';
            break;
        case 'da':
            localeOption = 'da-DK';
            break;
        case 'fi':
            localeOption = 'fi-FI';
            break;
        default:
            localeOption = 'en-GB';
    }

    return new Date(date).toLocaleString(localeOption, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: time ? '2-digit' : undefined,
        minute: time ? '2-digit' : undefined,
    });
};

// Returns YYYY-MM-DD. Slices ISO strings directly to avoid timezone offset shifting the date.
export const dateToISO = (date: Date | string): string => {
    if (typeof date === 'string' && date[10] === 'T') {
        return date.substring(0, 10);
    }
    return dayjs(date).format('YYYY-MM-DD');
};

export const secondsToHHMMSS = (seconds: number) => {
    const hours = dayjs.duration(seconds * 1000).hours();
    const minutes = dayjs.duration(seconds * 1000).minutes();
    const secs = dayjs.duration(seconds * 1000).seconds();

    let res = '';

    if (hours > 0) {
        res += `${hours.toString().padStart(2, '0')}:`;
    }

    res += `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

    return res;
};
